/**
 * Content sanitization utilities for user-edited scripts
 * Uses DOMPurify for robust XSS prevention
 */

import DOMPurify from 'dompurify';

const MAX_CONTENT_LENGTH = 10000;

// Configure DOMPurify to only allow basic formatting tags
const PURIFY_CONFIG: DOMPurify.Config = {
  ALLOWED_TAGS: ['b', 'i', 'br', 'p', 'strong', 'em'],
  ALLOWED_ATTR: [],
  KEEP_CONTENT: true,
};

/**
 * Normalizes literal "\n" strings to actual newline characters
 * and cleans up excessive whitespace
 */
export const normalizeNewlines = (content: string): string => {
  if (!content) return '';

  // Convert <br> / <br/> / <br /> tags to newlines (legacy data)
  let normalized = content.replace(/<br\s*\/?>/gi, '\n');

  // Replace literal \n\n and \n with actual newlines
  normalized = normalized
    .replace(/\\n\\n/g, '\n\n')
    .replace(/\\n/g, '\n');

  // Clean up excessive newlines (more than 2 consecutive)
  normalized = normalized.replace(/\n{3,}/g, '\n\n');

  return normalized.trim();
};

interface SanitizationResult {
  isValid: boolean;
  sanitizedContent: string;
  error?: string;
}

/**
 * Sanitizes HTML content using DOMPurify, keeping only safe formatting tags.
 * Preserves newlines as-is (rendered via whitespace-pre-wrap in UI).
 */
export const sanitizeWithAllowedTags = (content: string): string => {
  return DOMPurify.sanitize(content, PURIFY_CONFIG);
};

/**
 * Strips all HTML tags, returning plain text.
 */
export const stripDangerousTags = (content: string): string => {
  return DOMPurify.sanitize(content, { ALLOWED_TAGS: [], KEEP_CONTENT: true });
};

/**
 * Main sanitization function for user-edited content.
 * Returns sanitized content that is safe for rendering.
 */
export const sanitizeContent = (content: string): SanitizationResult => {
  if (content.length > MAX_CONTENT_LENGTH) {
    return {
      isValid: false,
      sanitizedContent: '',
      error: `Scenariusz jest za długi (max ${MAX_CONTENT_LENGTH.toLocaleString()} znaków)`,
    };
  }

  const sanitizedContent = sanitizeWithAllowedTags(content);

  return {
    isValid: true,
    sanitizedContent,
  };
};

/**
 * Validates content before saving
 */
export const validateContent = (content: string): { isValid: boolean; error?: string } => {
  if (!content || content.trim().length === 0) {
    return { isValid: false, error: 'Treść nie może być pusta' };
  }

  if (content.length > MAX_CONTENT_LENGTH) {
    return {
      isValid: false,
      error: `Scenariusz jest za długi (max ${MAX_CONTENT_LENGTH.toLocaleString()} znaków)`,
    };
  }

  return { isValid: true };
};

/**
 * Counts words in content (for reading time estimation)
 */
export const countWords = (content: string): number => {
  // Remove HTML tags for accurate count using DOMPurify
  const textOnly = DOMPurify.sanitize(content, { ALLOWED_TAGS: [], KEEP_CONTENT: true });
  const words = textOnly.trim().split(/\s+/).filter(word => word.length > 0);
  return words.length;
};

/**
 * Estimates reading time in seconds based on word count and speed
 * Average reading speed: ~150 words per minute for teleprompter
 */
export const estimateReadingTime = (wordCount: number, speedMultiplier: number = 1): number => {
  const wordsPerMinute = 150 * speedMultiplier;
  return Math.ceil((wordCount / wordsPerMinute) * 60);
};

/**
 * Formats time in MM:SS format
 */
export const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};
